/** SUNTRONIX ONE — Global Logistics Console.
 *
 *  Configure cross-border logistics lanes: IOR / EOR / customs broker /
 *  Incoterm / declared-value floor / restricted-flag.  Mirrors the
 *  Contracts editor pattern.
 */
import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/app/PageHeader";
import Section from "@/components/app/Section";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus, Save, ArrowLeft, AlertTriangle, Globe2, Clock } from "lucide-react";

const emptyLane = () => ({
  origin_country: "", destination_country: "",
  ior_party: "", eor_party: "", customs_broker: "",
  incoterm: "", duties_paid_by: "",
  declared_value_min: "", insurance_required: false,
  restricted: false, notes: "",
});

export default function LogisticsConsole() {
  const { masterClientId } = useParams();
  const [rows, setRows] = useState([]);
  const [incoterms, setIncoterms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [mc, setMc] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [mcRes, lRes] = await Promise.all([
        api.get(`/master-clients/${masterClientId}`),
        api.get(`/data-trust/logistics/${masterClientId}`),
      ]);
      setMc(mcRes.data);
      setRows(lRes.data.items || []);
      setIncoterms(lRes.data.incoterms || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not load logistics lanes");
    } finally { setLoading(false); }
  }, [masterClientId]);

  useEffect(() => { load(); }, [load]);

  const update = (idx, patch) => setRows((r) =>
    r.map((row, i) => i === idx ? { ...row, ...patch } : row));

  const add = () => setRows((r) => [...r, emptyLane()]);
  const remove = (idx) => setRows((r) => r.filter((_, i) => i !== idx));

  const saveOne = async (row) => {
    const payload = {
      master_company_id: masterClientId,
      origin_country: (row.origin_country || "").toUpperCase(),
      destination_country: (row.destination_country || "").toUpperCase(),
      ior_party: row.ior_party || null,
      eor_party: row.eor_party || null,
      customs_broker: row.customs_broker || null,
      incoterm: row.incoterm || null,
      duties_paid_by: row.duties_paid_by || null,
      declared_value_min: row.declared_value_min === ""
        ? null : Number(row.declared_value_min),
      insurance_required: !!row.insurance_required,
      restricted: !!row.restricted,
      notes: row.notes || null,
    };
    if (!payload.origin_country || !payload.destination_country) {
      throw new Error("Origin + destination required");
    }
    await api.post("/data-trust/logistics", payload);
  };

  const save = async () => {
    setSaving(true);
    let saved = 0, errors = 0;
    for (const row of rows) {
      try { await saveOne(row); saved++; }
      catch (e) {
        errors++;
        toast.error(`${row.origin_country}→${row.destination_country || "?"}: ${
          e.response?.data?.detail || e.message || "save failed"}`);
      }
    }
    if (saved) toast.success(`${saved} lane${saved > 1 ? "s" : ""} saved`);
    setSaving(false);
    if (!errors) load();
  };

  const restrictedCount = rows.filter((r) => r.restricted).length;

  return (
    <div className="space-y-6 max-w-[1400px]" data-testid="logistics-console">
      <PageHeader
        eyebrow={<Link to={`/customers/${masterClientId}`}
          className="flex items-center gap-1 hover:text-slate-900">
          <ArrowLeft className="h-3 w-3" /> Back to customer
        </Link>}
        title={mc ? `Logistics · ${mc.name || mc.legal_name || masterClientId}` : "Logistics"}
        context="Cross-border movement is gated by these lanes. Missing lanes block movement (super-admin override is logged to the Force-Override report)."
        actions={
          <Button onClick={save} disabled={saving || rows.length === 0}
            data-testid="logistics-save">
            {saving ? <Clock className="h-3.5 w-3.5 animate-spin mr-1.5" /> :
              <Save className="h-3.5 w-3.5 mr-1.5" />} Save all lanes
          </Button>
        }
        testId="logistics-console-header"
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Kpi label="Configured lanes" n={rows.length} icon={Globe2}
          testId="kpi-lanes" />
        <Kpi label="Restricted"       n={restrictedCount}
          tint={restrictedCount ? "text-red-700" : "text-slate-900"}
          icon={AlertTriangle} testId="kpi-restricted" />
        <Kpi label="Incoterms in use"
          n={new Set(rows.map((r) => r.incoterm).filter(Boolean)).size}
          testId="kpi-incoterms" />
        <Kpi label="With customs broker"
          n={rows.filter((r) => r.customs_broker).length}
          testId="kpi-broker" />
      </div>

      <Section title={`${rows.length} lane${rows.length === 1 ? "" : "s"}`}
        testId="logistics-lanes-section">
        <div className="st-panel">
          {loading ? (
            <div className="text-slate-400 text-center py-10">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="text-slate-500 text-center py-10 text-[13px]">
              No lanes configured yet.  Add one below.
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((r, idx) => (
                <div key={idx} className="p-4 space-y-3"
                  data-testid={`lane-row-${idx}`}>
                  <div className="flex items-center gap-3">
                    <div className="text-[11px] uppercase tracking-wider text-slate-500 w-16">
                      Lane {idx + 1}
                    </div>
                    {r.restricted && (
                      <span className="text-[11px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 bg-red-50 text-red-700 border-red-200">
                        <AlertTriangle className="h-3 w-3" /> Restricted
                      </span>
                    )}
                    <div className="ml-auto">
                      <Button variant="ghost" size="sm" onClick={() => remove(idx)}
                        data-testid={`lane-remove-${idx}`}>
                        <Trash2 className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-[12px]">
                    <Field label="Origin country (ISO2)">
                      <Input value={r.origin_country}
                        onChange={(e) => update(idx, { origin_country: e.target.value.toUpperCase().slice(0, 2) })}
                        placeholder="IN" maxLength={2}
                        data-testid={`lane-${idx}-origin`} />
                    </Field>
                    <Field label="Destination country (ISO2)">
                      <Input value={r.destination_country}
                        onChange={(e) => update(idx, { destination_country: e.target.value.toUpperCase().slice(0, 2) })}
                        placeholder="US" maxLength={2}
                        data-testid={`lane-${idx}-destination`} />
                    </Field>
                    <Field label="Incoterm">
                      <select className="input" value={r.incoterm || ""}
                        onChange={(e) => update(idx, { incoterm: e.target.value })}
                        data-testid={`lane-${idx}-incoterm`}>
                        <option value="">(none)</option>
                        {incoterms.map((it) => (
                          <option key={it} value={it}>{it}</option>
                        ))}
                      </select>
                    </Field>
                    <Field label="IOR party">
                      <Input value={r.ior_party || ""}
                        onChange={(e) => update(idx, { ior_party: e.target.value })}
                        placeholder="Suntronix / Customer / Partner-X"
                        data-testid={`lane-${idx}-ior`} />
                    </Field>
                    <Field label="EOR party">
                      <Input value={r.eor_party || ""}
                        onChange={(e) => update(idx, { eor_party: e.target.value })}
                        data-testid={`lane-${idx}-eor`} />
                    </Field>
                    <Field label="Customs broker">
                      <Input value={r.customs_broker || ""}
                        onChange={(e) => update(idx, { customs_broker: e.target.value })}
                        data-testid={`lane-${idx}-broker`} />
                    </Field>
                    <Field label="Duties paid by">
                      <select className="input" value={r.duties_paid_by || ""}
                        onChange={(e) => update(idx, { duties_paid_by: e.target.value })}
                        data-testid={`lane-${idx}-duties`}>
                        <option value="">(default)</option>
                        <option value="shipper">Shipper</option>
                        <option value="consignee">Consignee</option>
                      </select>
                    </Field>
                    <Field label="Declared value floor">
                      <Input type="number" value={r.declared_value_min ?? ""}
                        onChange={(e) => update(idx, { declared_value_min: e.target.value })}
                        placeholder="0.00"
                        data-testid={`lane-${idx}-declared`} />
                    </Field>
                    <div className="flex items-center gap-4 pt-4">
                      <label className="flex items-center gap-2 text-[12px]">
                        <input type="checkbox" checked={!!r.insurance_required}
                          onChange={(e) => update(idx, { insurance_required: e.target.checked })}
                          data-testid={`lane-${idx}-insurance`} />
                        Insurance required
                      </label>
                      <label className="flex items-center gap-2 text-[12px]">
                        <input type="checkbox" checked={!!r.restricted}
                          onChange={(e) => update(idx, { restricted: e.target.checked })}
                          data-testid={`lane-${idx}-restricted`} />
                        Restricted
                      </label>
                    </div>
                    <div className="md:col-span-3">
                      <Field label="Notes">
                        <Input value={r.notes || ""}
                          onChange={(e) => update(idx, { notes: e.target.value })}
                          placeholder="Special customs procedure, embargoed items, HS codes…"
                          data-testid={`lane-${idx}-notes`} />
                      </Field>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="p-3 border-t border-slate-100">
            <Button variant="create" onClick={add} data-testid="lane-add">
              <Plus className="h-3.5 w-3.5 mr-1.5" /> Add lane
            </Button>
          </div>
        </div>
      </Section>
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
    {children}
  </div>
);

const Kpi = ({ label, n, tint = "text-slate-900", icon: Icon, testId }) => (
  <div className="st-panel p-3" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
      {Icon && <Icon className="h-3 w-3" />}
      {label}
    </div>
    <div className={"text-2xl font-heading font-semibold mt-1 " + tint}>{n}</div>
  </div>
);
